import { createTheme, ThemeOptions } from '@mui/material/styles'

// `kidMode` reskins the theme with larger touch targets, rounder corners, and
// slightly warmer/lighter accent. Layered as an override on the standard theme
// so all the existing component styles (Card, Button, Dialog, etc.) stay in
// sync — we change radii/sizes only, not the component contracts.
export function getTheme(mode: 'light' | 'dark', kidMode = false) {
  const isDark = mode === 'dark'

  // White-first "modern & focused" palette — teal only. Secondary aliases
  // primary so leftover `color="secondary"` still reads as brand teal
  // (streaks, due chips, Practice use primary / outlined primary).
  const colors = isDark
    ? {
        bg: '#0B0F14', // cool charcoal, not forest green
        paper: '#141A22',
        elevated: '#1B232E',
        border: 'rgba(148,163,184,0.14)',
        borderStrong: 'rgba(148,163,184,0.28)',
        text: '#E5E7EB',
        textMuted: '#94A3B8',
        textSubtle: '#64748B',
        accent: '#2DD4BF', // teal-400, lifted for dark contrast
        accentHover: '#5EEAD4',
        accentFg: '#0B0F14',
        accent2: '#2DD4BF',
        accent2Strong: '#5EEAD4',
        accent2Soft: 'rgba(45,212,191,0.14)',
        accent2SoftBorder: 'rgba(94,234,212,0.38)',
        hoverBg: 'rgba(45,212,191,0.08)',
        selectedBg: 'rgba(45,212,191,0.14)',
      }
    : {
        bg: '#FAFAFA',
        paper: '#FFFFFF',
        elevated: '#F3F4F6',
        border: '#E5E7EB',
        borderStrong: '#D1D5DB',
        text: '#111827',
        textMuted: '#6B7280',
        textSubtle: '#9CA3AF',
        accent: '#0F766E', // deep teal
        accentHover: '#115E59',
        accentFg: '#FFFFFF',
        accent2: '#0F766E',
        accent2Strong: '#115E59',
        accent2Soft: '#F0FDFA',
        accent2SoftBorder: '#99F6E4',
        hoverBg: 'rgba(15,118,110,0.06)',
        selectedBg: 'rgba(15,118,110,0.10)',
      }

  const options: ThemeOptions = {
    palette: {
      mode,
      primary: { main: colors.accent, dark: colors.accentHover, contrastText: colors.accentFg },
      secondary: {
        main: colors.accent2,
        dark: colors.accent2Strong,
        contrastText: colors.accentFg,
      },
      success: { main: isDark ? '#4ADE80' : '#16A34A' },
      error: { main: isDark ? '#FCA5A5' : '#B91C1C' },
      warning: { main: isDark ? '#FBBF24' : '#B45309' },
      info: { main: isDark ? '#7DD3FC' : '#0369A1' },
      background: { default: colors.bg, paper: colors.paper },
      text: { primary: colors.text, secondary: colors.textMuted },
      divider: colors.border,
    },
    typography: {
      fontFamily: ['DM Sans', 'system-ui', '-apple-system', 'Segoe UI', 'Roboto', 'sans-serif'].join(','),
      h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 },
      h2: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 },
      h3: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
      h4: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.25 },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600, letterSpacing: '-0.005em' },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      body1: { lineHeight: 1.6 },
      body2: { lineHeight: 1.6 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0' },
      caption: { letterSpacing: '0.02em' },
      overline: { letterSpacing: '0.08em', fontWeight: 600 },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${colors.borderStrong} transparent`,
            backgroundColor: colors.bg,
          },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: colors.borderStrong,
            borderRadius: 4,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            backgroundImage: 'none',
            backgroundColor: colors.paper,
            // Warm, paper-like depth in light mode; calm flat surface in dark
            boxShadow: isDark
              ? 'none'
              : '0 1px 2px rgba(17,24,39,0.04), 0 1px 0 rgba(255,255,255,0.6) inset',
            transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              borderColor: colors.borderStrong,
              boxShadow: isDark
                ? 'none'
                : '0 2px 6px rgba(17,24,39,0.06), 0 1px 0 rgba(255,255,255,0.6) inset',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: 'none',
          },
          outlined: {
            borderColor: colors.border,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            backgroundColor: isDark ? 'rgba(11,15,20,0.75)' : 'rgba(255,255,255,0.80)',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: colors.paper,
            borderRight: `1px solid ${colors.border}`,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            textTransform: 'none',
            padding: '9px 18px',
            transition: 'all 0.15s ease',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          // NOTE: no hardcoded `contained` background/color here. MUI's own
          // default `contained` styling already derives bg/hover/text from
          // the palette per `color` prop (primary/secondary/error/...). A
          // hardcoded override here used to clobber every non-primary
          // contained button (secondary and error both rendered teal).
          outlined: {
            borderColor: colors.borderStrong,
            color: colors.text,
            '&:hover': {
              borderColor: colors.text,
              backgroundColor: 'transparent',
            },
          },
          text: {
            color: colors.text,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            color: colors.text,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiTextField: {
        // Distinctive focus state: 4px primary-coloured halo + primary
        // border + subtle inset shadow. Replaces the previous neutral-grey
        // focus ring so brand colour comes through at the moment of
        // interaction, matching the new Switch's focus halo.
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: 'transparent',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
              '& fieldset': {
                borderColor: colors.border,
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': { borderColor: colors.borderStrong },
              '&.Mui-focused fieldset': {
                borderColor: colors.accent,
                borderWidth: 1.5,
              },
              '&.Mui-focused': {
                backgroundColor: isDark
                  ? 'rgba(45,212,191,0.04)'
                  : 'rgba(15,118,110,0.025)',
                boxShadow: isDark
                  ? `0 0 0 4px rgba(45,212,191,0.18)`
                  : `0 0 0 4px rgba(15,118,110,0.14)`,
              },
              '&.Mui-error.Mui-focused': {
                boxShadow: `0 0 0 4px rgba(239,68,68,0.18)`,
              },
            },
          },
        },
      },
      MuiCheckbox: {
        // Custom checkbox: same design language as the redesigned Switch —
        // brand primary gradient on the active state, layered shadows for
        // depth, bouncy animation, subtle hover scale. MUI's default
        // checkbox is functional but feels generic next to a premium switch.
        styleOverrides: {
          root: {
            padding: 8,
            color: colors.borderStrong,
            transition: 'color 0.18s ease, background-color 0.18s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.08)',
              },
            },
            '&:active .MuiSvgIcon-root': {
              transform: 'scale(0.94)',
            },
            '& .MuiSvgIcon-root': {
              transition:
                'transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.2s ease',
              fontSize: 22,
              borderRadius: 4,
            },
            '&.Mui-checked': {
              color: colors.accent,
              '& .MuiSvgIcon-root': {
                // Drop shadow gives the checked box a touch of lift —
                // mirrors the Switch thumb shadow language.
                filter: isDark
                  ? 'drop-shadow(0 1px 3px rgba(45,212,191,0.35))'
                  : 'drop-shadow(0 1px 3px rgba(15,118,110,0.30))',
              },
            },
            '&.Mui-focusVisible': {
              backgroundColor: isDark
                ? 'rgba(45,212,191,0.16)'
                : 'rgba(15,118,110,0.12)',
              borderRadius: 8,
            },
            '&.Mui-disabled': { opacity: 0.5 },
          },
        },
      },
      MuiRadio: {
        // Custom radio: same family as Checkbox — primary fill, hover/press
        // scale, focus halo. Avoids MUI's default flat-grey-then-flat-color
        // transition by adding motion and a small drop shadow on active.
        styleOverrides: {
          root: {
            padding: 8,
            color: colors.borderStrong,
            transition: 'color 0.18s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.08)',
              },
            },
            '&:active .MuiSvgIcon-root': {
              transform: 'scale(0.94)',
            },
            '& .MuiSvgIcon-root': {
              transition:
                'transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.2s ease',
              fontSize: 22,
            },
            '&.Mui-checked': {
              color: colors.accent,
              '& .MuiSvgIcon-root': {
                filter: isDark
                  ? 'drop-shadow(0 1px 3px rgba(45,212,191,0.35))'
                  : 'drop-shadow(0 1px 3px rgba(15,118,110,0.30))',
              },
            },
            '&.Mui-focusVisible': {
              backgroundColor: isDark
                ? 'rgba(45,212,191,0.16)'
                : 'rgba(15,118,110,0.12)',
              borderRadius: '50%',
            },
            '&.Mui-disabled': { opacity: 0.5 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.75rem',
            height: 26,
          },
          outlined: {
            borderColor: colors.border,
            color: colors.textMuted,
            '&:hover': {
              borderColor: colors.borderStrong,
              backgroundColor: colors.hoverBg,
            },
          },
          // Scoped: `color="default"` (unset) filled chips keep the brand
          // teal look verbatim; any other color (secondary/error/...) falls
          // through to MUI's own per-color filled styling instead of being
          // clobbered with teal.
          filled: ({ ownerState }) =>
            ownerState.color === 'default'
              ? {
                  backgroundColor: colors.accent,
                  color: colors.accentFg,
                  border: 'none',
                  '&:hover': {
                    backgroundColor: colors.accent,
                    opacity: 0.85,
                  },
                }
              : { border: 'none' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 44,
            color: colors.textMuted,
            '&.Mui-selected': { color: colors.text },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.accent,
            height: 2,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 6px',
            '&:hover': { backgroundColor: colors.hoverBg },
            '&.Mui-selected': {
              backgroundColor: colors.selectedBg,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
              },
            },
          },
        },
      },
      MuiSelect: {
        // Match the new TextField focus state — outlined Select uses the
        // same OutlinedInput under the hood, but a few selectors need
        // explicit reinforcement so the focus halo + brand-coloured border
        // come through reliably.
        styleOverrides: {
          outlined: { borderRadius: 10 },
        },
      },
      MuiOutlinedInput: {
        // Catches OutlinedInput consumers that don't go through TextField
        // (raw `<Select variant="outlined">`, `<OutlinedInput>`, etc.) so
        // they get the same brand-halo focus treatment.
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
            '&.Mui-focused': {
              backgroundColor: isDark
                ? 'rgba(45,212,191,0.04)'
                : 'rgba(15,118,110,0.025)',
              boxShadow: isDark
                ? `0 0 0 4px rgba(45,212,191,0.18)`
                : `0 0 0 4px rgba(15,118,110,0.14)`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.accent,
                borderWidth: 1.5,
              },
            },
          },
        },
      },
      MuiSlider: {
        // Refreshed slider: gradient track, larger thumb with primary
        // halo on hover/active, inset rail shadow for depth. Same design
        // language as the new Switch (gradient + glow + bouncy motion).
        styleOverrides: {
          root: {
            color: colors.accent,
            height: 6,
            padding: '13px 0',
          },
          rail: {
            // Soft neutral rail with a faint inset shadow — the active
            // track "sits in" the rail rather than sitting on top.
            backgroundColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(17,24,39,0.14)',
            opacity: 1,
            boxShadow: isDark
              ? 'inset 0 1px 2px rgba(0,0,0,0.35)'
              : 'inset 0 1px 2px rgba(0,0,0,0.08)',
          },
          track: {
            // Gradient fill — primary → primary.dark — mirrors the Switch's
            // ON-state gradient.
            background: `linear-gradient(90deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
            border: 'none',
            boxShadow: isDark
              ? '0 0 8px rgba(45,212,191,0.25)'
              : '0 0 8px rgba(15,118,110,0.18)',
          },
          thumb: {
            backgroundColor: '#ffffff',
            width: 20,
            height: 20,
            border: `2px solid ${colors.accent}`,
            // Layered shadow + inner highlight — same recipe as the Switch
            // thumb. Larger thumb (20px vs 16px) for easier drag on touch.
            boxShadow: isDark
              ? '0 2px 6px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset'
              : '0 2px 4px rgba(0,0,0,0.22), 0 1px 1px rgba(0,0,0,0.10), 0 0 0 1px rgba(255,255,255,0.6) inset',
            transition:
              'transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.18s ease',
            '&:hover, &.Mui-focusVisible': {
              transform: 'scale(1.08)',
              boxShadow: isDark
                ? '0 0 0 8px rgba(45,212,191,0.2), 0 2px 6px rgba(0,0,0,0.55)'
                : '0 0 0 8px rgba(15,118,110,0.14), 0 2px 6px rgba(0,0,0,0.22)',
            },
            '&.Mui-active': {
              transform: 'scale(1.14)',
              boxShadow: isDark
                ? '0 0 0 12px rgba(45,212,191,0.25), 0 2px 8px rgba(0,0,0,0.6)'
                : '0 0 0 12px rgba(15,118,110,0.18), 0 2px 8px rgba(0,0,0,0.25)',
            },
          },
          valueLabel: {
            // Round-pill value label in primary, sits cleanly above the thumb.
            backgroundColor: colors.accent,
            color: colors.accentFg,
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 12,
            padding: '4px 8px',
            '&:before': { display: 'none' },
          },
          mark: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(17,24,39,0.22)',
            height: 6,
            width: 2,
            borderRadius: 1,
          },
          markActive: {
            backgroundColor: colors.accent,
            opacity: 0.85,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            height: 6,
            backgroundColor: colors.border,
          },
          bar: { borderRadius: 999, backgroundColor: colors.accent },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(11,15,20,0.85)' : 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            borderTop: `1px solid ${colors.border}`,
            height: 60,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 56,
            padding: '6px 0',
            color: colors.textMuted,
            '&.Mui-selected': { color: colors.accent },
          },
          label: {
            fontSize: '0.65rem',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '0.7rem',
              fontWeight: 600,
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: colors.accent,
            color: colors.accentFg,
            boxShadow: isDark
              ? '0 4px 20px rgba(0,0,0,0.5)'
              : '0 4px 20px rgba(0,0,0,0.12)',
            '&:hover': {
              backgroundColor: colors.accentHover,
              boxShadow: isDark
                ? '0 6px 24px rgba(0,0,0,0.6)'
                : '0 6px 24px rgba(15,118,110,0.25)',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: colors.border },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.text,
            color: colors.accentFg,
            fontWeight: 500,
            fontSize: '0.75rem',
            borderRadius: 8,
            padding: '6px 10px',
          },
          arrow: { color: colors.text },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${colors.border}`,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 6px',
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            color: colors.text,
            fontWeight: 600,
          },
        },
      },
      MuiSwitch: {
        // Custom premium switch: 56×32 pill, ON-state has a primary→darker
        // gradient track with a soft glow, the thumb is a 26px circle with
        // a layered drop shadow, an inner highlight on the off-state track
        // gives depth, the thumb scales on hover/press (cubic-bezier with
        // a slight overshoot for a "bouncy" feel), and the focus ring is
        // a coloured halo that matches the active brand.
        styleOverrides: {
          root: {
            width: 56,
            height: 32,
            padding: 0,
            marginRight: 4,
            overflow: 'visible',
          },
          switchBase: {
            padding: 4,
            // Spring-like cubic-bezier with a subtle overshoot — gives the
            // thumb a soft "click" feel as it lands.
            transition:
              'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.22s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              '& .MuiSwitch-thumb': {
                transform: 'scale(1.08)',
              },
            },
            '&:active .MuiSwitch-thumb': {
              transform: 'scale(0.94)',
            },
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              color: '#ffffff',
              '& .MuiSwitch-thumb': {
                // Boost the thumb shadow when ON so it really pops against
                // the bright track. Stays subtle in dark mode.
                boxShadow: isDark
                  ? '0 3px 10px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset'
                  : '0 3px 8px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.5) inset',
              },
              '& + .MuiSwitch-track': {
                opacity: 1,
                border: 0,
                // Gradient track in the ON state — primary → primary.dark
                // for visual interest beyond a flat fill.
                background: isDark
                  ? `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`
                  : `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                // Subtle outer glow when ON — picks up the brand colour.
                boxShadow: isDark
                  ? `0 0 0 1px rgba(45,212,191,0.25), 0 2px 8px rgba(45,212,191,0.18)`
                  : `0 0 0 1px rgba(15,118,110,0.18), 0 2px 8px rgba(15,118,110,0.20)`,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              boxShadow: isDark
                ? `0 0 0 4px rgba(45,212,191,0.3), 0 3px 10px rgba(0,0,0,0.7)`
                : `0 0 0 4px rgba(15,118,110,0.22), 0 3px 8px rgba(0,0,0,0.22)`,
            },
            '&.Mui-disabled': {
              opacity: 0.5,
              '& .MuiSwitch-thumb': { boxShadow: 'none' },
            },
          },
          thumb: {
            width: 24,
            height: 24,
            // Pure white thumb in both modes — matches every premium app
            // convention and reads cleanly off both the off-state grey and
            // the on-state gradient.
            backgroundColor: '#ffffff',
            // Layered shadow: outer for depth + inner highlight at the top
            // for a soft sheen.
            boxShadow: isDark
              ? '0 2px 6px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset'
              : '0 2px 4px rgba(0,0,0,0.22), 0 1px 1px rgba(0,0,0,0.10), 0 0 0 1px rgba(255,255,255,0.6) inset',
            transition:
              'transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.22s ease, background-color 0.22s ease',
          },
          track: {
            borderRadius: 18,
            // Off-state track: soft neutral with a faint inner shadow at
            // the top to suggest depth (the thumb "sits in" the track).
            backgroundColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(17,24,39,0.16)',
            boxShadow: isDark
              ? 'inset 0 1px 2px rgba(0,0,0,0.35)'
              : 'inset 0 1px 2px rgba(0,0,0,0.10)',
            opacity: 1,
            transition: 'background-color 0.22s ease, box-shadow 0.22s ease',
          },
        },
      },
      MuiFormControlLabel: {
        // Reserve real breathing room between the switch/checkbox and the
        // label — the MUI default (`margin-left: -11px` to align the
        // ripple) results in barely-any visible gap. We override to a
        // positive gap so the row reads as "[control] [text]" rather than
        // "[control][text]".
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginRight: 0,
            gap: 12,
          },
          label: {
            // Inherit the body weight; settings rows look more deliberate
            // when the label is a touch heavier than the default 400.
            fontWeight: 500,
          },
        },
      },
    },
  }

  const theme = createTheme(options)
  if (!kidMode) return theme

  // Kid-mode delta: larger rounding, bigger buttons + icon buttons, beefier
  // body text. Built by `createTheme(base, override)` so the kid overrides
  // merge into the base instead of replacing it — keeps every component
  // style from the standard theme intact.
  return createTheme(theme, {
    shape: { borderRadius: 20 },
    typography: {
      // 18px body, slightly looser leading so young readers don't get walls
      // of text. h1/h2 stay punchy for hero headings on the kid home.
      body1: { fontSize: '1.125rem', lineHeight: 1.65 },
      body2: { fontSize: '1rem', lineHeight: 1.6 },
      button: { fontWeight: 700, fontSize: '1rem' },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            padding: '12px 24px',
            minHeight: 56, // Apple HIG tap-target floor + a bit; comfortable for small hands
          },
          sizeSmall: { minHeight: 44, padding: '8px 16px' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            padding: 12,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 20 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 24 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 14, height: 32, fontSize: '0.875rem' },
        },
      },
    },
  })
}


